import { useMemo } from 'react';
import { useIntegrations } from '@/queries/useIntegrations';
import { useStoreData } from '@/queries/useStoreData';
import { useUser } from '@/queries/useUser';
import { INTEGRATION_STATUS, PRODUCT_TYPES } from '@/lib/constants';
import type { IntegrationsResponse } from '@/types';
import type { ServerPageData } from '@/features/product-editor/types';

type Product = ServerPageData['product'];

/**
 * Get list of errored integration types from the integrations response
 */
const getErroredIntegrations = (integrations: IntegrationsResponse | undefined): string[] => {
    if (!integrations) return [];

    return Object.values(integrations)
        .filter(integration => integration.status === INTEGRATION_STATUS.ERRORED)
        .map(integration => integration.type);
};

/**
 * Check if a meeting product has an errored integration
 */
const checkIfMeetingProductHasErroredIntegration = (product: Product, erroredIntegrations: string[]): boolean => {
    if (!product?.meeting?.platform) return false;
    return erroredIntegrations.includes(product.meeting.platform);
};

/**
 * Check if a product has an errored integration
 */
const checkIfProductHasErroredIntegration = (product: Product | undefined, erroredIntegrations: string[]): boolean => {
    if (!product) return false;

    switch (product.type) {
        case PRODUCT_TYPES.BOOKING:
            return checkIfMeetingProductHasErroredIntegration(product, erroredIntegrations);
        default:
            return false;
    }
};

/**
 * Hook to check if any product has an errored integration
 * This is used to show a warning icon on the Integrations tab
 */
export const useIntegrationErrors = () => {
    const { user } = useUser();
    const { data: integrations } = useIntegrations();
    const { data: storeData } = useStoreData(user?.default_store_id);

    const erroredIntegrations = useMemo(() => getErroredIntegrations(integrations), [integrations]);

    const hasProductWithErroredIntegration = useMemo(() => {
        if (!storeData || storeData.length === 0) return false;

        const store = storeData[0];
        if (!store?.pages) return false;

        for (const page of store.pages) {
            if (checkIfProductHasErroredIntegration(page.data?.product, erroredIntegrations)) {
                return true;
            }
        }
        return false;
    }, [storeData, erroredIntegrations]);

    const hasErroredIntegrations = erroredIntegrations.length > 0;

    return {
        hasProductWithErroredIntegration,
        hasErroredIntegrations,
        erroredIntegrations,
    };
};
